import {
  type QueryOptions,
  useQuery,
  type UseQueryResult,
} from '@tanstack/react-query';
import { z } from 'zod';
import { type ApiError } from '../../../../utils/api/ApiError/ApiError';
import { fetcher } from '../../../../utils/api/fetcher/fetcher';

// Define the zod schema for FilteredContent & nested objects

export const metadataShape = z
  .object({
    title: z.string(),
  })
  .passthrough();

export const nestedAssetFamilyShape = z
  .object({
    assetFamilyId: z.string(),
  })
  .passthrough();

export const digitalAssetDtoMetadataShape = z
  .object({
    pcmId: z.string(),
    displayTitle: z.string().optional(),
    description: z.string().optional(),
    assetType: z.string(),
    assetTypeLabel: z.string(),
    contentType: z.string(),
    contentTypeLabel: z.string(),
    lexile: z.string().optional(),
    standards: z.array(z.string()).optional(),
    assetFamilies: z.array(nestedAssetFamilyShape).optional(),
  })
  .passthrough();

export const teacherDtoShape = z
  .object({
    id: z.string(),
    instruction: z.string(),
  })
  .passthrough();

export const relatedAssetFamilyDtoShape = z
  .object({
    assetFamilyId: z.string(),
  })
  .passthrough();

export const digitalAssetDtoShape = z
  .object({
    pcmId: z.string(),
    label: z.string().optional(),
    metadata: digitalAssetDtoMetadataShape,
    teacher: teacherDtoShape.optional(),
    assetFamilies: z.array(relatedAssetFamilyDtoShape).optional(),
  })
  .passthrough();

export const linkItemShape = z
  .object({
    title: z.string(),
    href: z.string(),
  })
  .passthrough();

export const subSectionDtoShape = z
  .object({
    id: z.string(),
    parentId: z.string().optional(),
    metadata: metadataShape,
    purpose: z.string().optional(),
    isHighlighted: z.boolean(),
    digitalAssets: z.array(digitalAssetDtoShape).optional(),
  })
  .passthrough();

export const filteredContentShape = z
  .object({
    id: z.string().optional(),
    parentId: z.string().optional(),
    type: z.string(),
    title: z.string().optional(),
    purpose: z.string().optional(),
    teacher: teacherDtoShape.optional(),
    isHighlighted: z.boolean(),
    subSections: z.array(subSectionDtoShape).optional(),
    digitalAssets: z.array(digitalAssetDtoShape).optional(),
    links: z.array(linkItemShape).optional(),
    showAssets: z.boolean().optional(),
    flattenSubsections: z.boolean().optional(),
  })
  .passthrough();

const schema = z.array(filteredContentShape);

export type FilteredContent = z.infer<typeof filteredContentShape>;
export type SubSection = z.infer<typeof subSectionDtoShape>;
export type LinkItem = z.infer<typeof linkItemShape>;

export type GetFilteredContentApiParams = {
  product: string;
  filterId: string;
};

async function getFilteredContentApi({
  product,
  filterId,
}: GetFilteredContentApiParams) {
  const content = await fetcher(`/products/${product}/content`, {
    service: '/composite',
    version: '/v1',
    queryParameters: [['filterId', filterId]] as string[][],
  });
  return schema.parse(content);
}

export const getFilteredContentApiQueryKey = (
  args?: GetFilteredContentApiParams
) => {
  const { product, filterId } = args ?? {};

  return ['/v1', 'filtered-content', product, filterId].filter(Boolean);
};

export function useGetFilteredContentApi(
  args: GetFilteredContentApiParams,
  customQueryOptions?: QueryOptions<FilteredContent[], ApiError>
): UseQueryResult<FilteredContent[], ApiError> {
  return useQuery({
    queryKey: getFilteredContentApiQueryKey(args),
    queryFn: () => getFilteredContentApi(args),
    enabled: !!args.product && !!args.filterId,
    staleTime: 90000, // Match the original staleTime
    ...customQueryOptions,
  });
}
